import type { Role } from './roles'
import { type Permission, normalizePermissionName } from './permissions'
import type { Partner } from './partners'

export type UserRole = string
export type UserPermission = string

type UserPermissionValue = UserPermission | Pick<Permission, 'id' | 'name'>
type UserRoleObject = Pick<Role, 'id' | 'name'> & {
  permissions?: UserPermissionValue[]
}
type UserRoleValue = UserRole | UserRoleObject
type UserPartnerValue = Pick<Partner, 'id' | 'name'>

type UserWithRolePermissions = {
  roles?: UserRoleValue[]
}

export interface User {
  id: number
  name: string
  email: string
  partnerId: number | null
  role?: UserRole
  roles: UserRole[]
  roleIds: number[]
  permissions: UserPermission[]
  permissionIds: number[]
  partners: Partner[]
}

export function normalizeRoles(
  user: Partial<Pick<User, 'role'>> & { roles?: UserRoleValue[] },
): UserRole[] {
  const roleCandidates = Array.isArray(user.roles)
    ? user.roles.map((role) => (typeof role === 'string' ? role : role.name))
    : []
  const fallbackRole = user.role ? [user.role] : []

  return Array.from(new Set([...roleCandidates, ...fallbackRole]))
}

export function normalizePermissions(
  user: {
    permissions?: UserPermissionValue[]
  } & UserWithRolePermissions,
) {
  const directPermissions = Array.isArray(user.permissions)
    ? user.permissions
    : []
  const rolePermissions = Array.isArray(user.roles)
    ? user.roles.flatMap((role) =>
        typeof role === 'string' || !Array.isArray(role.permissions)
          ? []
          : role.permissions,
      )
    : []

  return Array.from(
    new Set(
      [...directPermissions, ...rolePermissions].map((permission) =>
        normalizePermissionName(
          typeof permission === 'string' ? permission : permission.name,
        ),
      ),
    ),
  )
}

export function normalizeUser(
  user: Omit<
    User,
    'roles' | 'roleIds' | 'permissions' | 'permissionIds' | 'partners'
  > & {
    partner_id?: number | null
    partner?: UserPartnerValue | null
    roles?: UserRoleValue[]
    roleIds?: number[]
    permissions?: UserPermissionValue[]
    permissionIds?: number[]
    partners?: Partner[]
  },
) {
  const roles = normalizeRoles(user)
  const roleIds = Array.isArray(user.roles)
    ? user.roles.flatMap((role) => (typeof role === 'string' ? [] : [role.id]))
    : (user.roleIds ?? [])
  const permissions = normalizePermissions(user)
  const permissionIds = Array.isArray(user.permissions)
    ? user.permissions.flatMap((permission) =>
        typeof permission === 'string' ? [] : [permission.id],
      )
    : Array.isArray(user.roles)
      ? Array.from(
          new Set(
            user.roles.flatMap((role) =>
              typeof role === 'string' || !Array.isArray(role.permissions)
                ? []
                : role.permissions.flatMap((permission) =>
                    typeof permission === 'string' ? [] : [permission.id],
                  ),
            ),
          ),
        )
      : (user.permissionIds ?? [])

  return {
    ...user,
    partnerId: user.partnerId ?? user.partner_id ?? user.partner?.id ?? null,
    role: user.role ?? roles[0],
    roles,
    roleIds,
    permissions,
    permissionIds,
    partners: user.partners ?? [],
  }
}

export function isAdminUser(
  user: Partial<Pick<User, 'role' | 'roles'>> | null,
) {
  if (!user) return false
  return normalizeRoles(user).includes('admin')
}
